import React, { useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, Search } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const selStyle = {
  padding: '8px 10px', borderRadius: '8px', background: '#000',
  border: '1px solid rgba(0, 255, 255, 0.3)', color: '#fff',
  fontSize: '13px', flex: 1, minWidth: 0
};

const MobileSearchResultsModal = ({
  isOpen, onClose, results, isSearching,
  query, setQuery, userType, setUserType, genre, setGenre,
  searchUsers, onViewProfile, onFollowToggle, onFriendRequest, onViewMutuals,
  initialScrollTop = 0
}) => {
  const listRef = useRef(null);

  useEffect(() => {
    if (isOpen && listRef.current) {
      listRef.current.scrollTop = initialScrollTop || 0;
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const avatarUrl = (pic) => pic ? `${BACKEND_URL}${pic.replace('/uploads/', '/api/uploads/')}` : null;

  return ReactDOM.createPortal(
    <div
      data-testid="mobile-search-results-modal"
      style={{
        position: 'fixed', inset: 0, zIndex: 9000,
        background: 'linear-gradient(170deg, #0a0a1f 0%, #12122b 100%)',
        display: 'flex', flexDirection: 'column'
      }}
    >
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0.9rem 1rem', borderBottom: '1px solid rgba(0, 212, 255, 0.2)', flexShrink: 0
      }}>
        <span style={{ color: '#00d4ff', fontWeight: 700, fontSize: '1rem' }}>
          Search Results{results.length > 0 ? ` (${results.length})` : ''}
        </span>
        <button
          onClick={onClose}
          data-testid="mobile-search-close-btn"
          style={{
            background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: '50%',
            width: '34px', height: '34px', display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', cursor: 'pointer', flexShrink: 0
          }}
        >
          <X size={19} />
        </button>
      </div>

      {/* Search controls */}
      <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid rgba(0, 212, 255, 0.1)', flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Search size={15} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.4)' }} />
            <input
              type="text"
              value={query}
              placeholder="Search by name..."
              data-testid="mobile-search-input"
              onChange={(e) => {
                setQuery(e.target.value);
                if (e.target.value.length > 2) {
                  searchUsers(e.target.value, { userType, genre });
                }
              }}
              style={{
                width: '100%', boxSizing: 'border-box', padding: '9px 10px 9px 32px',
                borderRadius: '8px', background: 'rgba(0,0,0,0.5)',
                border: '1px solid rgba(0, 255, 255, 0.3)', color: '#fff', fontSize: '14px'
              }}
            />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <select
            value={userType}
            data-testid="mobile-search-usertype"
            onChange={(e) => { setUserType(e.target.value); searchUsers(query, { userType: e.target.value, genre }); }}
            style={selStyle}
          >
            <option value="all">User Type</option>
            <option value="dj">DJs</option>
            <option value="producer">Producers</option>
            <option value="streamer">Streamers</option>
            <option value="mod">Mods</option>
            <option value="listener">Listeners</option>
          </select>
          <select
            value={genre}
            data-testid="mobile-search-genre"
            onChange={(e) => { setGenre(e.target.value); searchUsers(query, { userType, genre: e.target.value }); }}
            style={selStyle}
          >
            <option value="all">Genre</option>
            <option value="house">House</option>
            <option value="techno">Techno</option>
            <option value="trance">Trance</option>
            <option value="drum and bass">Drum & Bass</option>
            <option value="dubstep">Dubstep</option>
            <option value="edm">EDM</option>
            <option value="deep house">Deep House</option>
            <option value="progressive">Progressive</option>
            <option value="minimal">Minimal</option>
            <option value="electro">Electro</option>
          </select>
          <button
            onClick={() => searchUsers('', { userType, genre, randomDiscover: true })}
            disabled={isSearching}
            data-testid="mobile-discover-random-btn"
            style={{
              padding: '8px 10px', borderRadius: '8px', background: 'rgba(236, 72, 153, 0.15)',
              border: '1px solid rgba(236, 72, 153, 0.5)', color: '#ec4899',
              fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer'
            }}
          >
            🎲
          </button>
        </div>
      </div>

      {/* Results list */}
      <div ref={listRef} style={{ flex: 1, overflowY: 'auto', padding: '0.75rem 1rem 5.5rem', WebkitOverflowScrolling: 'touch' }}>
        {isSearching ? (
          <p style={{ color: 'rgba(255,255,255,0.6)', textAlign: 'center', padding: '2rem 0' }}>Searching...</p>
        ) : results.length === 0 ? (
          <p style={{ color: 'rgba(255,255,255,0.6)', textAlign: 'center', padding: '2rem 0' }}>No Beatseekers found — try a different search</p>
        ) : (
          results.map(u => (
            <div
              key={u.id}
              data-testid={`mobile-result-${u.id}`}
              style={{
                padding: '0.85rem', marginBottom: '0.6rem', borderRadius: '12px',
                background: 'rgba(26, 26, 46, 0.7)', border: '1px solid rgba(0, 212, 255, 0.15)'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.6rem' }}>
                <div style={{
                  width: '52px', height: '52px', minWidth: '52px', borderRadius: '50%', overflow: 'hidden',
                  background: 'linear-gradient(135deg, #00d4ff, #0099cc)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontWeight: 700, fontSize: '1.25rem', flexShrink: 0
                }}>
                  {avatarUrl(u.profile_picture) ? (
                    <img src={avatarUrl(u.profile_picture)} alt={u.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    u.name?.charAt(0).toUpperCase() || 'U'
                  )}
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {u.name}
                    {['dj', 'producer', 'streamer', 'mod'].includes(u.user_type?.toLowerCase()) && (
                      <span style={{ color: '#fbbf24', fontSize: '0.72rem', fontWeight: 500, marginLeft: '6px', textTransform: 'capitalize' }}>
                        {u.user_type}
                      </span>
                    )}
                  </div>
                  <div style={{ color: '#9ca3af', fontSize: '0.78rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {u.email}
                  </div>
                  {u.mutual_friends_count > 0 && (
                    <div
                      onClick={() => onViewMutuals(u)}
                      data-testid={`mobile-mutual-hint-${u.id}`}
                      style={{ color: '#00d4ff', fontSize: '0.72rem', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer' }}
                    >
                      🤝 {u.mutual_friends_count} mutual {u.mutual_friends_count === 1 ? 'friend' : 'friends'}
                      <span style={{ color: '#fff', fontWeight: 600 }}> · View</span>
                    </div>
                  )}
                  {u.shared_genres?.length > 0 && (
                    <div style={{ color: '#fbbf24', fontSize: '0.72rem', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      🎧 You both like {u.shared_genres.join(', ')}
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={() => onViewProfile(u, listRef.current ? listRef.current.scrollTop : 0)}
                data-testid={`mobile-view-profile-${u.id}`}
                style={{
                  width: '100%', padding: '8px', borderRadius: '8px', border: 'none',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#fff', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer', marginBottom: '6px'
                }}
              >
                👤 View Profile
              </button>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button
                  onClick={() => onFollowToggle(u)}
                  data-testid={`mobile-follow-btn-${u.id}`}
                  style={{
                    flex: 1, minWidth: 0, padding: '8px 4px', borderRadius: '8px',
                    background: u.is_following ? 'rgba(34, 197, 94, 0.15)' : 'rgba(0, 212, 255, 0.1)',
                    border: u.is_following ? '1px solid rgba(34, 197, 94, 0.6)' : '1px solid #00d4ff',
                    color: u.is_following ? '#4ade80' : '#00d4ff',
                    fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                  }}
                >
                  {u.is_following ? '✓ Following' : 'Follow'}
                </button>
                <button
                  onClick={() => u.friend_status !== 'request_sent' && onFriendRequest(u)}
                  disabled={u.friend_status === 'request_sent'}
                  data-testid={`mobile-add-friend-btn-${u.id}`}
                  style={{
                    flex: 1, minWidth: 0, padding: '8px 4px', borderRadius: '8px',
                    background: 'rgba(255, 51, 51, 0.1)', border: '1px solid #ff3333', color: '#ff3333',
                    fontWeight: 600, fontSize: '0.8rem',
                    cursor: u.friend_status === 'request_sent' ? 'default' : 'pointer',
                    opacity: u.friend_status === 'request_sent' ? 0.6 : 1,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                  }}
                >
                  {u.friend_status === 'request_sent' ? 'Request Sent' : 'Add Friend'}
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>,
    document.body
  );
};

export default MobileSearchResultsModal;
